import type { LiveQuote } from "~/v3-poc/api/snapshotStream";
import type { Command } from "./command";
import type { Effect } from "./effect";
import { type ClientState, QUOTE_FLUSH_MS, type WorkerState } from "./state";

export interface FoldResult {
  state: WorkerState;
  effects: Effect[];
}

const unchanged = (state: WorkerState): FoldResult => ({ state, effects: [] });

const unionSymbols = (clients: ReadonlyMap<string, ClientState>): string[] => {
  const union = new Set<string>();
  for (const client of clients.values()) {
    for (const symbol of client.symbols) {
      union.add(symbol);
    }
  }
  return [...union].sort();
};

const sameSymbols = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((symbol, index) => symbol === b[index]);

/** Drops cached quotes for symbols nobody subscribes to any more. */
const prune = (
  quotes: ReadonlyMap<string, LiveQuote>,
  symbols: readonly string[],
): ReadonlyMap<string, LiveQuote> => {
  const wanted = new Set(symbols);
  let stale = false;
  for (const symbol of quotes.keys()) {
    if (!wanted.has(symbol)) {
      stale = true;
      break;
    }
  }
  if (!stale) {
    return quotes;
  }
  const next = new Map<string, LiveQuote>();
  for (const symbol of symbols) {
    const quote = quotes.get(symbol);
    if (quote !== undefined) {
      next.set(symbol, quote);
    }
  }
  return next;
};

/**
 * The only producer of socket lifecycle effects. A socket is wanted when an
 * account and at least one client are known — matching today's provider, which
 * opens as soon as an account exists regardless of symbols.
 */
const reconcile = (state: WorkerState): FoldResult => {
  const { account } = state;

  if (account === undefined || state.clients.size === 0) {
    if (state.socket === "idle") {
      return unchanged(state);
    }
    return {
      state: { ...state, socket: "idle", sent: [] },
      effects: [{ kind: "socket.close" }],
    };
  }

  const symbols = unionSymbols(state.clients);

  if (state.socket === "idle") {
    return {
      state: { ...state, socket: "started", sent: symbols },
      effects: [
        { kind: "socket.symbols", symbols: [...symbols] },
        { kind: "socket.open", url: account.socketUrl },
      ],
    };
  }

  if (sameSymbols(symbols, state.sent)) {
    return unchanged(state);
  }

  return {
    state: {
      ...state,
      sent: symbols,
      lastQuotes: prune(state.lastQuotes, symbols),
    },
    effects: [{ kind: "socket.symbols", symbols: [...symbols] }],
  };
};

const withClient = (
  state: WorkerState,
  clientId: string,
  client: ClientState,
): WorkerState => {
  const clients = new Map(state.clients);
  clients.set(clientId, client);
  return { ...state, clients };
};

/** Delivers a client the cached quotes for the given symbols, if any exist. */
const warmStart = (
  state: WorkerState,
  clientId: string,
  symbols: readonly string[],
): Effect[] => {
  const { account } = state;
  // `lastQuotes` is only ever populated while an account is set (see
  // `handleQuotesReceived`) and is wiped on every account change, so a
  // cached quote with no account is not reachable. Guard explicitly anyway,
  // rather than asserting the account is present.
  if (account === undefined) {
    return [];
  }
  const quotes: Record<string, LiveQuote> = {};
  for (const symbol of symbols) {
    const quote = state.lastQuotes.get(symbol);
    if (quote !== undefined) {
      // Copy: the fold is agnostic about a quote's shape, so a spread (not
      // named fields) is correct here. `lastQuotes` and `pending` share this
      // exact object by design — only the effect payload gets its own copy,
      // so a consumer mutating a delivered quote can't corrupt either cache.
      quotes[symbol] = { ...quote };
    }
  }
  return Object.keys(quotes).length === 0
    ? []
    : [{ kind: "quotes.deliver", clientId, account: account.key, quotes }];
};

const handleClientConnect = (
  state: WorkerState,
  command: Extract<Command, { kind: "client.connect" }>,
): FoldResult => {
  if (state.clients.has(command.clientId)) {
    return unchanged(state);
  }
  return reconcile(
    withClient(state, command.clientId, { visible: true, symbols: [] }),
  );
};

const handleClientDisconnect = (
  state: WorkerState,
  command: Extract<Command, { kind: "client.disconnect" }>,
): FoldResult => {
  if (!state.clients.has(command.clientId)) {
    return unchanged(state);
  }
  const clients = new Map(state.clients);
  clients.delete(command.clientId);
  if (clients.size > 0) {
    return reconcile({ ...state, clients });
  }
  const clearing: Effect[] =
    state.flushTimer === "armed"
      ? [{ kind: "timer.clear", timerId: "quotes.flush" }]
      : [];
  const result = reconcile({
    ...state,
    clients,
    pending: new Map(),
    flushTimer: "idle",
  });
  return { state: result.state, effects: [...result.effects, ...clearing] };
};

const handleClientVisibility = (
  state: WorkerState,
  command: Extract<Command, { kind: "client.visibility" }>,
): FoldResult => {
  const client = state.clients.get(command.clientId);
  if (client === undefined || client.visible === command.visible) {
    return unchanged(state);
  }
  return unchanged(
    withClient(state, command.clientId, {
      ...client,
      visible: command.visible,
    }),
  );
};

const handleClientSymbols = (
  state: WorkerState,
  command: Extract<Command, { kind: "client.symbols" }>,
): FoldResult => {
  const client = state.clients.get(command.clientId);
  if (client === undefined) {
    return unchanged(state);
  }
  const symbols = [...new Set(command.symbols)].sort();
  // Only what this client did not already have. Re-sending a symbol it is
  // holding would push an identical value into its quote store, and the
  // store writes a fresh object per symbol, so the widget selecting that
  // symbol would re-render for nothing on every watchlist edit.
  const held = new Set(client.symbols);
  const added = symbols.filter((symbol) => !held.has(symbol));
  const result = reconcile(
    withClient(state, command.clientId, { ...client, symbols }),
  );
  return {
    state: result.state,
    effects: [
      ...result.effects,
      ...warmStart(result.state, command.clientId, added),
    ],
  };
};

const handleAccountReport = (
  state: WorkerState,
  command: Extract<Command, { kind: "account.report" }>,
): FoldResult => {
  if (state.account?.key === command.account.key) {
    return unchanged(state);
  }
  const closing: Effect[] =
    state.socket === "started" ? [{ kind: "socket.close" }] : [];
  // Every cached quote belonged to the old account. Keeping them would warm
  // start the next client with another account's prices, and an armed timer
  // would flush the old account's pending batch after the switch.
  if (state.flushTimer === "armed") {
    closing.push({ kind: "timer.clear", timerId: "quotes.flush" });
  }
  const result = reconcile({
    ...state,
    // Copy so the returned state can never be mutated through the caller's command object.
    account: {
      key: command.account.key,
      socketUrl: command.account.socketUrl,
    },
    socket: "idle",
    sent: [],
    lastQuotes: new Map(),
    pending: new Map(),
    flushTimer: "idle",
  });
  return { state: result.state, effects: [...closing, ...result.effects] };
};

const handleQuotesReceived = (
  state: WorkerState,
  command: Extract<Command, { kind: "quotes.received" }>,
): FoldResult => {
  const entries = Object.entries(command.quotes);
  if (entries.length === 0) {
    return unchanged(state);
  }
  const lastQuotes = new Map(state.lastQuotes);
  const pending = new Map(state.pending);
  for (const [symbol, quote] of entries) {
    // Per-field, not whole-quote: `LiveQuote` fields are both optional and
    // the socket sets each only when the frame carried it, so a trade tick
    // is `{ price }` with no `previousClose`. Replacing would drop the close
    // and the watchlist's change % — it needs both fields — until the next
    // frame that happens to include one.
    const merged = { ...lastQuotes.get(symbol), ...quote };
    lastQuotes.set(symbol, merged);
    pending.set(symbol, merged);
  }
  const effects: Effect[] =
    state.flushTimer === "armed"
      ? []
      : [
          {
            kind: "timer.set",
            timerId: "quotes.flush",
            delayMs: QUOTE_FLUSH_MS,
          },
        ];
  return {
    state: { ...state, lastQuotes, pending, flushTimer: "armed" },
    effects,
  };
};

/** Builds one client's delivery from the pending batch, if it holds anything for them. */
const flushDelivery = (
  pending: ReadonlyMap<string, LiveQuote>,
  clientId: string,
  client: ClientState,
  accountKey: string,
): Effect | undefined => {
  const quotes: Record<string, LiveQuote> = {};
  for (const symbol of client.symbols) {
    const quote = pending.get(symbol);
    if (quote !== undefined) {
      // Copy: see warmStart above for why a spread, not named fields.
      quotes[symbol] = { ...quote };
    }
  }
  return Object.keys(quotes).length > 0
    ? { kind: "quotes.deliver", clientId, account: accountKey, quotes }
    : undefined;
};

const handleQuotesFlushDue = (state: WorkerState): FoldResult => {
  const { account } = state;
  const nextState: WorkerState = {
    ...state,
    pending: new Map(),
    flushTimer: "idle",
  };
  // `pending` is only ever populated while an account is set (see
  // `handleQuotesReceived`), so a non-empty pending batch with no account is
  // not reachable. Guard explicitly anyway, rather than asserting it.
  if (account === undefined) {
    return { state: nextState, effects: [] };
  }
  const effects: Effect[] = [];
  for (const [clientId, client] of state.clients) {
    const delivery = flushDelivery(
      state.pending,
      clientId,
      client,
      account.key,
    );
    if (delivery !== undefined) {
      effects.push(delivery);
    }
  }
  return { state: nextState, effects };
};

export const fold = (state: WorkerState, command: Command): FoldResult => {
  switch (command.kind) {
    case "client.connect":
      return handleClientConnect(state, command);
    case "client.disconnect":
      return handleClientDisconnect(state, command);
    case "client.visibility":
      return handleClientVisibility(state, command);
    case "client.symbols":
      return handleClientSymbols(state, command);
    case "account.report":
      return handleAccountReport(state, command);
    case "quotes.received":
      return handleQuotesReceived(state, command);
    case "quotes.flushDue":
      return handleQuotesFlushDue(state);
  }
};
